import type { ContentPublishingDispatch } from "../types";
import { isEmailDispatch, summarizeEmailDispatch } from "./email";

export type DispatchPostSendEvent = {
  email: string;
  type: "bounced" | "complained";
  at: string;
};

const parseDispatchPostSendEvents = (
  dispatch: Pick<ContentPublishingDispatch, "response_snapshot">,
): DispatchPostSendEvent[] => {
  const res = (dispatch.response_snapshot ?? {}) as Record<string, unknown>;
  const raw = res.post_send_events;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(
      (e): e is DispatchPostSendEvent =>
        !!e &&
        typeof e === "object" &&
        typeof (e as { email?: unknown }).email === "string" &&
        ((e as { type?: unknown }).type === "bounced" || (e as { type?: unknown }).type === "complained") &&
        typeof (e as { at?: unknown }).at === "string",
    )
    .map((e) => ({ email: e.email.toLowerCase(), type: e.type, at: e.at }));
};

/**
 * Returns the count of post-send events (bounce/complaint) recorded AFTER the
 * dispatch finished. These are late events from the email provider — what the
 * "+N late" badge surfaces in the Activity feed.
 */
export const countLatePostSendEvents = (
  dispatch: Pick<ContentPublishingDispatch, "response_snapshot" | "completed_at" | "created_at">,
): { bounced: number; complained: number; total: number; latestAt: string | null } => {
  const events = parseDispatchPostSendEvents(dispatch);
  if (events.length === 0) return { bounced: 0, complained: 0, total: 0, latestAt: null };
  const referenceMs = (() => {
    const ref = dispatch.completed_at ?? dispatch.created_at;
    if (!ref) return 0;
    const t = new Date(ref).getTime();
    return Number.isFinite(t) ? t : 0;
  })();
  let bounced = 0;
  let complained = 0;
  let latestAt: string | null = null;
  for (const ev of events) {
    const t = new Date(ev.at).getTime();
    if (!Number.isFinite(t)) continue;
    // Late = strictly after the dispatch completed (or after created_at when
    // no completion time is available). Same-second events still count.
    if (referenceMs && t < referenceMs) continue;
    if (ev.type === "bounced") bounced += 1;
    else complained += 1;
    if (!latestAt || new Date(ev.at).getTime() > new Date(latestAt).getTime()) {
      latestAt = ev.at;
    }
  }
  return { bounced, complained, total: bounced + complained, latestAt };
};

// ===== 7-day connector delivery health =====

export type ConnectorDeliveryHealth = {
  windowDays: number;
  recipientsAttempted: number;
  accepted: number;
  rejected: number;
  bounced: number;
  complained: number;
  /** accepted / recipientsAttempted (post-bounce). null when no recipients. */
  deliveryRate: number | null;
  dispatches: number;
};

/**
 * Computes per-recipient delivery health for an email connector over the
 * given window. Bounces and complaints (including late-arriving ones via
 * post_send_events) are counted as failures alongside hard rejections.
 */
export const getConnectorDeliveryHealth = (
  connectorId: string,
  dispatches: ContentPublishingDispatch[],
  windowDays = 7,
): ConnectorDeliveryHealth => {
  const horizon = Date.now() - windowDays * 24 * 60 * 60 * 1000;
  const rows = dispatches.filter(
    (d) =>
      d.connector_id === connectorId &&
      isEmailDispatch(d) &&
      new Date(d.created_at).getTime() >= horizon,
  );
  let attempted = 0;
  let rejected = 0;
  let bounced = 0;
  let complained = 0;
  for (const d of rows) {
    const summary = summarizeEmailDispatch(d);
    attempted += summary.recipients.length;
    rejected += summary.rejectedCount;
    const events = parseDispatchPostSendEvents(d);
    // De-dup by email so multiple events for the same address don't
    // double-count against attempted recipients.
    const seen = new Set<string>();
    for (const ev of events) {
      if (seen.has(ev.email)) continue;
      seen.add(ev.email);
      if (ev.type === "bounced") bounced += 1;
      else complained += 1;
    }
  }
  const failures = rejected + bounced + complained;
  const accepted = Math.max(0, attempted - failures);
  return {
    windowDays,
    recipientsAttempted: attempted,
    accepted,
    rejected,
    bounced,
    complained,
    deliveryRate: attempted > 0 ? accepted / attempted : null,
    dispatches: rows.length,
  };
};
